// @ts-check
import ZeroDataGrid from '~/components/Shared/ZeroDataGrid/ZeroDataGrid';
import { useSubmissionDataGridContext } from '~/components/Forms/SubmissionDataGrid/SubmissionDataGrid.context';
import { useLocation } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { isEqual, keyBy } from "lodash-es";
import { DEFAULT_SORT_MODEL, EventNames, getColumnDefs, getSortByColumnName, loadSubmissions } from '~/components/Forms/SubmissionDataGrid/SubmissionDataGrid.utils';
import useZeroSelector from '~/hooks/useZeroSelector';
import { objectIsEmpty, setGridLoading } from '~/other/Helper';
import { useZeroEventListener } from '~/components/ZeroContext';
import { get_form } from '~/api/zero-api';
import { useCachedApiLoader } from '~/components/Util/CachedApiLoader';
import { getFormFieldsForDataGrid } from '~/other/agGridHelper';
import { getSubmissionAnswer } from '~/other/forms';
import { useDataGridSetup } from '~/components/Shared/ZeroDataGrid/ZeroDataGrid.hooks';


const SUBMISSIONS_PER_PAGE = 25;

function useSubmissionDataGridQuery() {
    const formsQuery = useZeroSelector(state => state.forms.query);
    const periodQuery = useZeroSelector(state => state.forms.period_query);
    const cachedApiLoader = useCachedApiLoader();
    const [query, setQuery] = useState({formsQuery, periodQuery, form: null, formFields: null});

    useEffect(() => {
        async function buildQuery() {
            let form = null;
            let formFields = null;
            try {
                const params = new URLSearchParams(formsQuery);
                const formIds = (params.get("form_uuid") ?? "").split(",").filter(v => v.length > 0);
                if (formIds.length === 1 && formIds[0]) {
                    const formId = formIds[0];
                    const data = await cachedApiLoader.fetch(() => get_form(formId), "", {cacheKey: `get_form:${formId}`,});
                    form = data.form;
                    formFields = getFormFieldsForDataGrid(form);
                }
            } catch (err) {
                console.error(err);
            }

            return {
                formsQuery,
                periodQuery,
                form,
                formFields,
            };
        }

        buildQuery().then(setQuery);
    }, [formsQuery, periodQuery]);

    return query;
}

export default function SubmissionDataGrid({formType}) {
    const location = useLocation();
    const {gridRef, settings, setSettings, rowCount} = useSubmissionDataGridContext();
    const query = useSubmissionDataGridQuery();

    const colDefs = useMemo(() => getColumnDefs(query.formFields?.headers), [query.formFields]);

    const getRows = useCallback(async (/** @type {AgGrid.IGetRowsParams} */params) => {
        const perPage = SUBMISSIONS_PER_PAGE;
        const currentPage = Math.floor((params.startRow ?? 0) / perPage) + 1;

        const { formsQuery, periodQuery, formFields } = query;

        const queryParams = new URLSearchParams(formsQuery + periodQuery);
        queryParams.set('page', String(currentPage));
        queryParams.set('full_data', 'false');
        queryParams.set('form_types', formType);

        if (!objectIsEmpty(params.filterModel)) {
            queryParams.set('ag_filter', JSON.stringify(params.filterModel));
        }

        params.sortModel.forEach(sortModel => {
            let col = getSortByColumnName(sortModel.colId);
            if (sortModel.sort === 'desc') {
                col = `-${col}`;
            }
            queryParams.append('sort_by', col);
        });

        const gridApi = gridRef.current?.api;
        if (rowCount === null) {
            setGridLoading(gridApi, true);
        }
        const {submissions, totalSubmissions} = await loadSubmissions(queryParams);

        if (formFields?.fields?.length) {
            for (const submission of submissions) {
                const submissionFields = keyBy(submission.fields, 'id');
                const answers = formFields.fields.map(formField => {
                    const { id } = formField;
                    
                    let value = "-";
                    if (submissionFields[id]) {
                        value = getSubmissionAnswer(formField, submissionFields[id]) ?? "";
                    }
    
                    return {id, value};
                });
                
                answers.forEach(answer => submission[answer.id] = answer.value);
            }
        }

        params.successCallback(submissions, totalSubmissions);
        if (rowCount === null) {
            setGridLoading(gridApi, false);
        }
    }, [rowCount, query, formType]);

    const clearSubmissionCache = useCallback(() => {
        // @ts-ignore
        loadSubmissions.cache.clear();
    }, [loadSubmissions]);

    const {onGridReady, onStateUpdated} = useDataGridSetup({
        eventNames: EventNames,
        autoLoadData: true,
        gridRef,
        colDefs,
        getRows,
        setSettings,
        onReset: clearSubmissionCache,
        onRefresh: clearSubmissionCache,
    });
    
    useEffect(() => {
        // Clear memoization when component is unmounted
        return () => {
            clearSubmissionCache();
        }
    }, []);

    return (
        <ZeroDataGrid
            gridRef={gridRef}
            columnDefs={colDefs}
            defaultColDef={{
                cellStyle: {
                    display: 'flex',
                    alignItems: 'center',
                }
            }}
            initialState={location.state?.gridState ?? settings}
            onGridReady={onGridReady}
            onStateUpdated={onStateUpdated}
            cacheBlockSize={SUBMISSIONS_PER_PAGE}
            cacheOverflowSize={2}
            maxConcurrentDatasourceRequests={2}
            infiniteInitialRowCount={rowCount ?? SUBMISSIONS_PER_PAGE}
            pagination={true}
            paginationPageSizeSelector={false}
            paginationPageSize={rowCount ?? SUBMISSIONS_PER_PAGE}
            tooltipShowDelay={500}
            tooltipShowMode="standard"
            suppressCellFocus
        />
    )
}